import api from "../_app/api";
import { parsePermissions } from "../permission/api";
import {
  GetUsersResponse,
  User,
  UserAccountAssignRequest,
  UserCreateUpdateRequest,
  UserDetails,
  UserId,
  UserListItem,
} from "./types";
import {
  GetUserCommand,
  GetUserCommandOutput,
  UpdateUserAttributesCommand,
  UpdateUserAttributesCommandOutput,
} from "@aws-sdk/client-cognito-identity-provider";
import { cognitoIdentityProviderClient } from "../_app/lib/cognito";
import { AttributeType } from "@aws-sdk/client-cognito-identity-provider/dist-types/models/models_0";
import { fetchAuthSession } from "aws-amplify/auth";

export const registerUser = async (accountCode: string, email: string) => {
  return await api({
    method: "PUT",
    url: "/users/register",
    data: {
      accountCode,
      email,
    },
    skip_bearer_injection: true,
    skip_token_refresh: true,
  });
};

export const getAllUsers = async (accountId: string, page = 0, size = 25) => {
  const { data }: { data: GetUsersResponse } = await api({
    method: "GET",
    url: "/users",
    params: { page, size, "account-id": accountId },
  });
  return data;
};

export const getUserById = async (accountId: string, userId: string) => {
  const { data }: { data: User } = await api({
    method: "GET",
    url: "/users/" + userId,
    params: { "account-id": accountId },
  });
  return parseUser(data);
};

export const getUserIdByEmail = async (accountId: string, email: string) => {
  const { data }: { data: UserId } = await api({
    method: "GET",
    url: `/users/${email}/id`,
    params: { "account-id": accountId },
  });

  return data;
};

export const createUser = async (accountId: string, userCreateUpdateRequest: UserCreateUpdateRequest) => {
  const { data }: { data: UserId } = await api({
    method: "POST",
    url: "/users",
    data: userCreateUpdateRequest,
    params: { "account-id": accountId },
  });

  return data;
};

export const updateUser = async (accountId: string, userCreateUpdateRequest: UserCreateUpdateRequest, userId: string) => {
  const { data } = await api({
    method: "PUT",
    url: `/users/${userId}`,
    data: userCreateUpdateRequest,
    params: { "account-id": accountId },
  });

  return data;
};

export const assignUser = async (accountId: string, userAccountAssignRequest: UserAccountAssignRequest, userId: string) => {
  const { data } = await api({
    method: "PUT",
    url: `/users/${userId}/accounts`,
    data: userAccountAssignRequest,
    params: { "account-id": accountId },
  });

  return data;
};

export const doesHubUserExist = async () => {
  try {
    const response = await api({
      method: "GET",
      url: "/users/my/exists",
    });

    return response.status === 204;
  } catch (error) {
    return false;
  }
};

export const updateMyUser = async ({ accountId, user }: { accountId: string; user: UserDetails }) => {
  await api({
    method: "PUT",
    url: "/users/my",
    params: { "account-id": accountId },
    data: {
      ...user,
    },
  });
};

export const deleteUser = async ({ accountId, user }: { accountId: string; user: UserListItem }) => {
  const { data }: { data: UserListItem } = await api({
    method: "DELETE",
    url: "/users/" + user?.id,
    params: { "account-id": accountId },
  });
  return data;
};

export const disableUser = async ({ accountId, user }: { accountId: string; user: UserListItem }) => {
  const { data }: { data: UserListItem } = await api({
    method: "PUT",
    url: "/users/" + user?.id + "/disable",
    params: { "account-id": accountId },
  });
  return data;
};

export const enableUser = async (accountId: string, user: UserListItem) => {
  const { data }: { data: UserListItem } = await api({
    method: "PUT",
    url: "/users/" + user?.id + "/enable",
    params: { "account-id": accountId },
  });
  return data;
};

export const resendWelcomeEmail = async (accountId: string, user: UserListItem) => {
  const { data }: { data: UserListItem } = await api({
    method: "POST",
    url: "/users/" + user?.id + "/email",
    params: { "account-id": accountId },
  });
  return data;
};

export const parseUser = (user: User) => {
  user.permissions = user?.permissions?.map(parsePermissions);
  return user;
};

export const getAccountsForLevel = async (accountId: string, level: string, params: any) => {
  const requestParams: any = {
    "account-id": accountId,
  };
  if (params?.parent) {
    requestParams.parent = params?.parent;
  }
  if (params?.subLevel) {
    // params.subLevel = subLevel;
    return;
  }
  if (params["search-term"]) {
    requestParams["search-term"] = params["search-term"];
  }
  if (params?.limit) {
    requestParams.limit = params?.limit;
  }
  if (params?.offset) {
    requestParams.offset = params?.offset;
  }

  const { data }: { data: any } = await api({
    method: "GET",
    url: "/accounts/" + level,
    params: requestParams,
  });
  return data;
};

export const updateCognitoUser = async (attributes: AttributeType[]): Promise<UpdateUserAttributesCommandOutput> => {
  const session = await fetchAuthSession();
  const command = new UpdateUserAttributesCommand({
    AccessToken: session?.tokens?.accessToken?.toString(),
    UserAttributes: attributes,
  });

  return cognitoIdentityProviderClient.send(command);
};

export const fetchUserAttributes = async (): Promise<GetUserCommandOutput> => {
  const session = await fetchAuthSession();
  const command = new GetUserCommand({
    AccessToken: session?.tokens?.accessToken?.toString(),
  });

  return cognitoIdentityProviderClient.send(command);
};
